import type { LogRateResponse } from '@central/api';
import { Card, color, formatDateTime } from '@central/ui';
import { describeRate, rateUnit } from '../lib/logRate';

const DAY_MS = 86_400_000;

/** "10:18" within a day's range, else "Oct 9, 10:18"; UTC like every API time. */
function axisTime(iso: string, spanMs: number): string {
  return spanMs <= DAY_MS ? iso.slice(11, 16) : formatDateTime(iso);
}

/**
 * "Matching lines per minute" bars over the searched range
 * (docs/design/AdminLogs.dc.html), from GET /logs/rate. Bar height is
 * relative to the busiest bucket.
 */
export function LogRateChart({ rate }: { rate: LogRateResponse }) {
  const { points, step } = rate;
  if (points.length === 0) return null;
  const first = points[0]!;
  const end = new Date(
    new Date(points[points.length - 1]!.at).getTime() + step * 1000,
  ).toISOString();
  const span = new Date(end).getTime() - new Date(first.at).getTime();
  const { total, peak } = describeRate(rate);
  const unit = rateUnit(step);
  const caption = peak
    ? `${total.toLocaleString('en-US')} line${total === 1 ? '' : 's'} · peak ${peak.count.toLocaleString('en-US')} at ${axisTime(peak.at, span)}`
    : 'No matching lines';

  return (
    <Card>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
          <b>Matching lines {unit}</b>
          <span style={{ color: color.textMuted }}>{caption}</span>
        </div>
        <div
          role="img"
          aria-label={`Matching lines ${unit}, ${axisTime(first.at, span)} to ${axisTime(end, span)}: ${caption}`}
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: points.length > 90 ? 1 : 2,
            height: 72,
            borderBottom: `1px solid ${color.borderStrong}`,
          }}
        >
          {points.map((p) => (
            <div
              key={p.at}
              data-count={p.count}
              title={`${axisTime(p.at, span)} · ${p.count.toLocaleString('en-US')}`}
              style={{
                flex: 1,
                minWidth: 0,
                height: peak ? `${(p.count / peak.count) * 100}%` : 0,
                background: color.link,
                borderRadius: '2px 2px 0 0',
              }}
            />
          ))}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 12,
            color: color.textMuted,
          }}
        >
          <span>{axisTime(first.at, span)}</span>
          <span>{axisTime(end, span)}</span>
        </div>
      </div>
    </Card>
  );
}
